import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { questionsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Header } from '../components/layout/Header';
import { GitBranch, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function CreateQuestionPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
  });

  // Wait for auth check before redirecting
  React.useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="spinner" data-testid="auth-loading"></div>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a question');
      return;
    }

    setLoading(true);

    try {
      const response = await questionsAPI.create({
        title: formData.title.trim(),
        description: formData.description.trim() || null,
      });
      
      toast.success('Mindmap created!');
      navigate(`/q/${response.data.question_id}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create mindmap';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      <div className="max-w-2xl mx-auto px-4 py-12" data-testid="create-question-page">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-8 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-orange-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Ask a Question</h1>
              <p className="text-slate-500">Start a new collaborative mindmap</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="title" className="text-slate-700 text-base">
                Your Question *
              </Label>
              <p className="text-sm text-slate-500 mt-1 mb-2">
                This will be the root of your mindmap
              </p>
              <Input
                id="title"
                name="title"
                type="text"
                required
                value={formData.title}
                onChange={handleChange}
                className="text-lg"
                placeholder="e.g., What should we study after 12th?"
                data-testid="question-title-input"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-slate-700 text-base">
                Description (optional)
              </Label>
              <p className="text-sm text-slate-500 mt-1 mb-2">
                Add context to help others understand what you're looking for
              </p>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="Add more details about your question..."
                data-testid="question-description-input"
              />
            </div>

            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-start gap-3">
                <GitBranch className="w-5 h-5 text-orange-500 mt-0.5" />
                <div>
                  <h3 className="font-medium text-slate-900">How it works</h3>
                  <p className="text-sm text-slate-600 mt-1">
                    Once you create this question, a mindmap will be generated with your 
                    question as the root node. Anyone can then add branches (child nodes) 
                    to build a comprehensive knowledge tree together.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1"
                data-testid="cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-accent"
                disabled={loading}
                data-testid="create-submit-btn"
              >
                {loading ? (
                  <div className="spinner w-5 h-5"></div>
                ) : (
                  'Create Mindmap'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
